import "@/App.css";
import "@/styles/checkit.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { CheckitAuthProvider } from "@/lib/checkit/CheckitAuthContext";
import { CheckitCurrencyProvider } from "@/lib/checkit/CheckitCurrencyContext";
import { FxProvider } from "@/components/CurrencySwitcher";
import ScrollToTop from "@/components/ScrollToTop";
import RequireCheckitAuth from "@/components/checkit/RequireCheckitAuth";
import { PUBLIC_TOOL_ROUTES } from "@/pages/checkit/public/routes";

// checkit.health — this deployment IS the checkit.health product.
const CheckitLanding = lazy(() => import("@/pages/checkit/CheckitLanding"));
const CheckitPricing = lazy(() => import("@/pages/checkit/CheckitPricing"));
const CheckitHowItWorks = lazy(() => import("@/pages/checkit/CheckitHowItWorks"));
const CheckitPrivacy = lazy(() => import("@/pages/checkit/CheckitPrivacy"));
const CheckitTerms = lazy(() => import("@/pages/checkit/CheckitTerms"));
const CheckitContact = lazy(() => import("@/pages/checkit/CheckitContact"));
const CheckitAuthPage = lazy(() => import("@/pages/checkit/CheckitAuthPage"));
const CheckitOAuthCallback = lazy(() => import("@/pages/checkit/CheckitOAuthCallback"));
const CheckitApp = lazy(() => import("@/pages/checkit/CheckitApp"));
const CheckitAccount = lazy(() => import("@/pages/checkit/CheckitAccount"));
const CheckitKidPrint = lazy(() => import("@/pages/checkit/CheckitKidPrint"));
const CheckitBillingReturn = lazy(() => import("@/pages/checkit/CheckitBillingReturn"));
const CheckitAdminLogin = lazy(() => import("@/pages/checkit/admin/CheckitAdminLogin"));
const CheckitAdmin = lazy(() => import("@/pages/checkit/admin/CheckitAdmin"));
const CheckitSystemManager = lazy(() => import("@/pages/checkit/admin/CheckitSystemManager"));

function PageLoader() {
  return (
    <div className="checkit-scope min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

// Legacy: any old /checkit/* link → strip the prefix and redirect to root path.
function StripCheckitPrefix() {
  const loc = useLocation();
  const to = (loc.pathname.replace(/^\/checkit/, "") || "/") + loc.search + loc.hash;
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CheckitAuthProvider>
            <CheckitCurrencyProvider>
              <FxProvider>
                <main className="flex-1">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<CheckitLanding />} />
                      <Route path="/pricing" element={<CheckitPricing />} />
                      <Route path="/how-it-works" element={<CheckitHowItWorks />} />
                      <Route path="/privacy" element={<CheckitPrivacy />} />
                      <Route path="/terms" element={<CheckitTerms />} />
                      <Route path="/contact" element={<CheckitContact />} />
                      {/* Public SEO calculator pages (no login) */}
                      {PUBLIC_TOOL_ROUTES.map(({ path, Component }) => (
                        <Route key={path} path={path} element={<Component />} />
                      ))}
                      <Route path="/login" element={<CheckitAuthPage mode="login" />} />
                      <Route path="/signup" element={<CheckitAuthPage mode="signup" />} />
                      <Route path="/auth/callback" element={<CheckitOAuthCallback />} />
                      <Route path="/billing/return" element={<CheckitBillingReturn />} />
                      <Route path="/app" element={<RequireCheckitAuth><CheckitApp /></RequireCheckitAuth>} />
                      <Route path="/account" element={<RequireCheckitAuth><CheckitAccount /></RequireCheckitAuth>} />
                      <Route path="/print/child" element={<RequireCheckitAuth><CheckitKidPrint /></RequireCheckitAuth>} />
                      <Route path="/admin/login" element={<CheckitAdminLogin />} />
                      <Route path="/admin/system" element={<CheckitSystemManager />} />
                      <Route path="/admin" element={<CheckitAdmin />} />
                      {/* Backwards-compat for previously deployed /checkit/* URLs */}
                      <Route path="/checkit/*" element={<StripCheckitPrefix />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </main>
                <Toaster position="bottom-right" richColors />
              </FxProvider>
            </CheckitCurrencyProvider>
          </CheckitAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
